import React from "react";
import {
  Grid,
  Typography,
  Card,
  CardContent,
  IconButton,
  Button,
  Divider,
  Box,
} from "@mui/material";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import YoutubeIcon from "@mui/icons-material/YouTube";
import X from "@mui/icons-material/X";
import SiteIcon from "@mui/icons-material/Language";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTiktok } from "@fortawesome/free-brands-svg-icons";

const TikTokIcon = () => (
  <FontAwesomeIcon icon={faTiktok} style={{ color: "#000", fontSize: 30 }} />
);

/**
 * Função melhorada para quebrar a string em parágrafos de forma inteligente
 * Cria parágrafos mais coesos, evitando fragmentação excessiva
 * Quebra apenas em mudanças significativas de contexto
 */
const toParagraphs = (text) => {
  if (!text) return [];

  // Remove \r e normaliza espaços múltiplos
  let normalized = text
    .replace(/\r/g, "")
    .replace(/ +/g, " ")
    .trim();

  // Primeiro, divide por quebras duplas de linha existentes
  let paragraphs = normalized.split(/\n{2,}/);

  // Para cada "parágrafo", verifica se precisa subdividir
  const finalParagraphs = [];

  paragraphs.forEach((para) => {
    // Remove quebras simples de linha e normaliza espaços
    para = para.replace(/\n/g, " ").trim();

    if (!para) return;

    // Se o parágrafo é longo (mais de 550 caracteres), 
    // tenta quebrar em mudanças de contexto temporal/narrativo
    if (para.length > 550) {
      // Padrões que indicam mudança significativa de contexto:
      // - Anos que marcam mudanças de década ou período (Ex: "Em 2004", "Em 2012", "Em 2016")
      // - "Nas eleições" (novo ciclo eleitoral)
      // Estratégia: detecta anos que estão distantes entre si (diferença de 4+ anos)
     // const yearPattern = /\.\s+Em (\d{4}),/g;
      const electionPattern = /\.\s+(Nas eleições)/g;
      
      const breakPoints = [];
      let match;
      
      // Encontra todos os anos mencionados
      const years = [];
      const yearMatches = [];
      let tempMatch;
      
      const yearRegex = /\.\s+Em (\d{4}),/g;
      while ((tempMatch = yearRegex.exec(para)) !== null) {
        years.push(parseInt(tempMatch[1]));
        yearMatches.push({ index: tempMatch.index, year: parseInt(tempMatch[1]) });
      }
      
      // Identifica quebras onde há salto temporal significativo (4+ anos)
      for (let i = 1; i < years.length; i++) {
        if (years[i] - years[i-1] >= 4) {
          breakPoints.push(yearMatches[i].index);
        }
      }
      
      // Adiciona quebras em "Nas eleições"
      while ((match = electionPattern.exec(para)) !== null) {
        breakPoints.push(match.index);
      }
      
      // Ordena e remove duplicatas
      const uniqueBreaks = [...new Set(breakPoints)].sort((a, b) => a - b);
      
      if (uniqueBreaks.length > 0) {
        const parts = [];
        let lastIndex = 0;
        
        for (const breakIndex of uniqueBreaks) {
          const segment = para.substring(lastIndex, breakIndex + 1).trim();
          // Só adiciona se o segmento tiver pelo menos 150 caracteres
          if (segment.length >= 150) {
            parts.push(segment);
            lastIndex = breakIndex + 1;
          }
        }
        
        // Adiciona o resto
        if (lastIndex < para.length) {
          const remaining = para.substring(lastIndex).trim();
          if (remaining.length > 0) {
            parts.push(remaining);
          }
        }
        
        // Se conseguiu dividir, usa as partes
        if (parts.length > 1) {
          parts.forEach(p => finalParagraphs.push(p));
        } else {
          finalParagraphs.push(para);
        }
      } else {
        finalParagraphs.push(para);
      }
    } else {
      finalParagraphs.push(para);
    }
  });

  return finalParagraphs.filter(Boolean);
};

const BiografiaVereador = ({
  imagem,
  nome,
  partido,
  nomeCivil,
  telefone,
  celular,
  email,
  gabinete,
  biografia,
  comissoes,
  facebookLink,
  instagramLink,
  youtubeLink,
  XLink,
  LinkSite,
  LinkTikTok,
  onBack,
}) => {
  const paragraphs = toParagraphs(biografia);

  return (
    <Box sx={{ padding: 2 }}>
      {/* Botão de Voltar */}
      <Button
        onClick={onBack}
        sx={{
          marginBottom: 2,
          color: "#FFFFFF",
          borderColor: "#FFFFFF",
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.1)",
            borderColor: "#FFFFFF",
          },
        }}
        variant="outlined"
        aria-label="Voltar para a lista de vereadores"
      >
        Voltar
      </Button>

      <Grid container spacing={4}>
        {/* Painel Esquerdo */}
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              padding: 2,
              textAlign: "center",
              boxShadow: 3,
              backgroundColor: "#f5f5f5",
              "&:hover": { outline: "2px solid #FFD700" },
            }}
          >
            <img
              src={imagem}
              alt={`Imagem do vereador ${nome}`}
              style={{
                width: "100%",
                maxWidth: "200px",
                height: "auto",
                borderRadius: "8px",
              }}
            />
            <Typography
              variant="h5"
              fontWeight="bold"
              sx={{ marginTop: 2 }}
              aria-label={`Vereador: ${nome}`}
            >
              {nome}
            </Typography>
            <Typography variant="body1" color="text.secondary" aria-label={`Partido: ${partido}`}>
              {partido}
            </Typography>

            <Divider sx={{ marginY: 2 }} />

            {/* Informações de Contato */}
            <Typography variant="body2"><strong>Nome civil:</strong> {nomeCivil}</Typography>
            <Typography variant="body2"><strong>Telefone(s):</strong> {telefone}</Typography>
            <Typography variant="body2"><strong>Celular:</strong> {celular}</Typography>
            <Typography variant="body2"><strong>E-mail:</strong> {email}</Typography>
            <Typography variant="body2"><strong>Gabinete:</strong> {gabinete}</Typography>

            <Divider sx={{ marginY: 2 }} />

            {/* Redes Sociais */}
            <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
              {facebookLink && (
                <IconButton component="a" href={facebookLink} target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                  <FacebookIcon sx={{ fontSize: "48px" }} />
                </IconButton>
              )}
              {instagramLink && (
                <IconButton component="a" href={instagramLink} target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                  <InstagramIcon sx={{ fontSize: "48px", color: "#E4405F" }} />
                </IconButton>
              )}
              {youtubeLink && (
                <IconButton component="a" href={youtubeLink} target="_blank" rel="noopener noreferrer" aria-label="YouTube">
                  <YoutubeIcon sx={{ fontSize: "54px", color: "#FF0000" }} />
                </IconButton>
              )}
              {XLink && (
                <IconButton component="a" href={XLink} target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)">
                  <X sx={{ fontSize: "40px", color: "#000" }} />
                </IconButton>
              )}
              {LinkTikTok && (
                <IconButton component="a" href={LinkTikTok} target="_blank" rel="noopener noreferrer" aria-label="TikTok">
                  <TikTokIcon />
                </IconButton>
              )}
              {LinkSite && (
                <IconButton component="a" href={LinkSite} target="_blank" rel="noopener noreferrer" aria-label="Site oficial">
                  <SiteIcon sx={{ fontSize: "48px", color: "#1976D2" }} />
                </IconButton>
              )}
            </Box>
          </Card>
        </Grid>

        {/* Painel Direito */}
        <Grid item xs={12} md={7} lg={5} sx={{ display: 'flex', justifyContent: 'center' }}>
          <Card
            sx={{
              width: '100%',
              maxWidth: 860,
              padding: 2,
              boxShadow: 3,
              backgroundColor: "#f5f5f5",
              "&:hover": { outline: "2px solid #FFD700" },
            }}
            role="region"
            aria-labelledby="biografia-title"
          >
            <CardContent>
              {/* Biografia */}
              <Typography id="biografia-title" variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Biografia
              </Typography>

              {/* Texto com indentação e parágrafos melhorados */}
              <Box
                component="section"
                sx={{
                  maxWidth: { xs: '68ch', lg: '72ch' },
                  mx: 'auto',
                  textAlign: "justify",
                  lineHeight: 1.85,
                  letterSpacing: "0.01em",
                  hyphens: "auto",
                }}
              >
                {paragraphs.map((p, i) => (
                  <Typography
                    key={i}
                    variant="body2"
                    color="text.secondary"
                    component="p"
                    sx={{
                      mb: 1.5, // Espaçamento aumentado entre parágrafos
                      textIndent: i === 0 ? 0 : "1.5em", // Indentação da primeira linha (exceto no primeiro parágrafo)
                    }}
                  >
                    {p}
                  </Typography>
                ))}
              </Box>

              <Divider sx={{ marginY: 2 }} />

              {/* Participação em Comissões */}
              <Typography variant="h6" fontWeight="bold" sx={{ marginBottom: 2 }}>
                Participação em Comissões
              </Typography>
              <Box>
                {comissoes.map((comissao, index) => (
                  <Box key={index} sx={{ marginBottom: 1 }}>
                    <Typography variant="body2">
                      <strong>Permanente:</strong> {comissao.permanente}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Cargo:</strong> {comissao.cargo}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BiografiaVereador;



// import React from "react";
// import {
//   Grid,
//   Typography,
//   Card,
//   CardContent,
//   IconButton,
//   Button,
//   Divider,
//   Box,
// } from "@mui/material";
// import FacebookIcon from "@mui/icons-material/Facebook";
// import InstagramIcon from "@mui/icons-material/Instagram";
// import YoutubeIcon from "@mui/icons-material/YouTube";
// import X from "@mui/icons-material/X";
// import SiteIcon from "@mui/icons-material/Language";
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import { faTiktok } from "@fortawesome/free-brands-svg-icons";

// const TikTokIcon = () => (
//   <FontAwesomeIcon icon={faTiktok} style={{ color: "#000", fontSize: 30 }} />
// );

// // Helper para quebrar a string em parágrafos
// const toParagraphs = (text) =>
//   (text || "")
//     .replace(/\r/g, "")
//     .split(/\n{2,}/)               // 2+ quebras = novo parágrafo
//     .map((p) => p.replace(/\n/g, " ").trim()) // quebra simples viro espaço
//     .filter(Boolean);

// const BiografiaVereador = ({
//   imagem,
//   nome,
//   partido,
//   nomeCivil,
//   telefone,
//   celular,
//   email,
//   gabinete,
//   biografia,
//   comissoes,
//   facebookLink,
//   instagramLink,
//   youtubeLink,
//   XLink,
//   LinkSite,
//   LinkTikTok,
//   onBack,
// }) => {
//   const paragraphs = toParagraphs(biografia);

//   return (
//     <Box sx={{ padding: 2 }}>
//       {/* Botão de Voltar */}
//       <Button
//         onClick={onBack}
//         sx={{
//           marginBottom: 2,
//           color: "#FFFFFF",
//           borderColor: "#FFFFFF",
//           "&:hover": {
//             backgroundColor: "rgba(255, 255, 255, 0.1)",
//             borderColor: "#FFFFFF",
//           },
//         }}
//         variant="outlined"
//         aria-label="Voltar para a lista de vereadores"
//       >
//         Voltar
//       </Button>

//       <Grid container spacing={4}>
//         {/* Painel Esquerdo */}
//         <Grid item xs={12} md={4}>
//           <Card
//             sx={{
//               padding: 2,
//               textAlign: "center",
//               boxShadow: 3,
//               backgroundColor: "#f5f5f5",
//               "&:hover": { outline: "2px solid #FFD700" },
//             }}
//           >
//             <img
//               src={imagem}
//               alt={`Imagem do vereador ${nome}`}
//               style={{
//                 width: "100%",
//                 maxWidth: "200px",
//                 height: "auto",
//                 borderRadius: "8px",
//               }}
//             />
//             <Typography
//               variant="h5"
//               fontWeight="bold"
//               sx={{ marginTop: 2 }}
//               aria-label={`Vereador: ${nome}`}
//             >
//               {nome}
//             </Typography>
//             <Typography variant="body1" color="text.secondary" aria-label={`Partido: ${partido}`}>
//               {partido}
//             </Typography>

//             <Divider sx={{ marginY: 2 }} />

//             {/* Informações de Contato */}
//             <Typography variant="body2"><strong>Nome civil:</strong> {nomeCivil}</Typography>
//             <Typography variant="body2"><strong>Telefone(s):</strong> {telefone}</Typography>
//             <Typography variant="body2"><strong>Celular:</strong> {celular}</Typography>
//             <Typography variant="body2"><strong>E-mail:</strong> {email}</Typography>
//             <Typography variant="body2"><strong>Gabinete:</strong> {gabinete}</Typography>

//             <Divider sx={{ marginY: 2 }} />

//             {/* Redes Sociais */}
//             <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
//               {facebookLink && (
//                 <IconButton component="a" href={facebookLink} target="_blank" rel="noopener noreferrer" aria-label="Facebook">
//                   <FacebookIcon sx={{ fontSize: "48px" }} />
//                 </IconButton>
//               )}
//               {instagramLink && (
//                 <IconButton component="a" href={instagramLink} target="_blank" rel="noopener noreferrer" aria-label="Instagram">
//                   <InstagramIcon sx={{ fontSize: "48px", color: "#E4405F" }} />
//                 </IconButton>
//               )}
//               {youtubeLink && (
//                 <IconButton component="a" href={youtubeLink} target="_blank" rel="noopener noreferrer" aria-label="YouTube">
//                   <YoutubeIcon sx={{ fontSize: "54px", color: "#FF0000" }} />
//                 </IconButton>
//               )}
//               {XLink && (
//                 <IconButton component="a" href={XLink} target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)">
//                   <X sx={{ fontSize: "40px", color: "#000" }} />
//                 </IconButton>
//               )}
//               {LinkTikTok && (
//                 <IconButton component="a" href={LinkTikTok} target="_blank" rel="noopener noreferrer" aria-label="TikTok">
//                   <TikTokIcon />
//                 </IconButton>
//               )}
//               {LinkSite && (
//                 <IconButton component="a" href={LinkSite} target="_blank" rel="noopener noreferrer" aria-label="Site oficial">
//                   <SiteIcon sx={{ fontSize: "48px", color: "#1976D2" }} />
//                 </IconButton>
//               )}
//             </Box>
//           </Card>
//         </Grid>

//         {/* Painel Direito */}
//         <Grid item xs={12} md={7} lg={5} sx={{ display: 'flex', justifyContent: 'center' }}>


//           <Card
//             sx={{
//               width: '100%',
//               maxWidth: 860,
//               padding: 2,
//               boxShadow: 3,
//               backgroundColor: "#f5f5f5",
//               "&:hover": { outline: "2px solid #FFD700" },
//             }}
//             role="region"
//             aria-labelledby="biografia-title"
//           >
//             <CardContent>
//               {/* Biografia */}
//               <Typography id="biografia-title" variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
//                 Biografia
//               </Typography>

//               {/* Texto com indentação e parágrafos */}
//               <Box
//                 component="section"
//                 sx={{

//                  // maxWidth: "75ch",
//                  maxWidth: { xs: '68ch', lg: '72ch' }, mx: 'auto',
//                   textAlign: "justify",
//                   lineHeight: 1.85,
//                   letterSpacing: "0.01em",
//                   hyphens: "auto",
//                 }}
//               >
//                 {paragraphs.map((p, i) => (
//                   <Typography
//                     key={i}
//                     variant="body2"
//                     color="text.secondary"
//                     component="p"
//                     sx={{
//                       mb: 1.2,
//                       textIndent: i === 0 ? 0 : "1.5em", // sem indent no 1º parágrafo
//                     }}
//                   >
//                     {p}
//                   </Typography>
//                 ))}
//               </Box>

//               <Divider sx={{ marginY: 2 }} />

//               {/* Participação em Comissões */}
//               <Typography variant="h6" fontWeight="bold" sx={{ marginBottom: 2 }}>
//                 Participação em Comissões
//               </Typography>
//               <Box>
//                 {comissoes.map((comissao, index) => (
//                   <Box key={index} sx={{ marginBottom: 1 }}>
//                     <Typography variant="body2">
//                       <strong>Permanente:</strong> {comissao.permanente}
//                     </Typography>
//                     <Typography variant="body2" color="text.secondary">
//                       <strong>Cargo:</strong> {comissao.cargo}
//                     </Typography>
//                   </Box>
//                 ))}
//               </Box>
//             </CardContent>
//           </Card>
//         </Grid>
//       </Grid>
//     </Box>
//   );
// };

// export default BiografiaVereador;



// // import React from "react";
// // import {
// //   Grid,
// //   Typography,
// //   Card,
// //   CardContent,
// //   IconButton,
// //   Button,
// //   Divider,
// //   Box,
// // } from "@mui/material";
// // import FacebookIcon from "@mui/icons-material/Facebook";
// // import InstagramIcon from "@mui/icons-material/Instagram";
// // import YoutubeIcon from "@mui/icons-material/YouTube";
// // import X from "@mui/icons-material/X";
// // import SiteIcon from "@mui/icons-material/Language";
// // import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// // import { faTiktok } from "@fortawesome/free-brands-svg-icons";

// // const TikTokIcon = () => (
// //   <FontAwesomeIcon icon={faTiktok} style={{ color: "#000", fontSize: 30 }} />
// // );

// // const BiografiaVereador = ({
// //   imagem,
// //   nome,
// //   partido,
// //   nomeCivil,
// //   telefone,
// //   celular,
// //   email,
// //   gabinete,
// //   biografia,
// //   comissoes,
// //   facebookLink,
// //   instagramLink,
// //   youtubeLink,
// //   XLink,
// //   LinkSite,
// //   LinkTikTok,
// //   onBack,
// // }) => {
// //   return (
// //     <Box sx={{ padding: 2 }}>
// //       {/* Botão de Voltar */}
// //       <Button
// //         onClick={onBack}
// //         sx={{
// //           marginBottom: 2,
// //           color: "#FFFFFF",
// //           borderColor: "#FFFFFF",
// //           "&:hover": {
// //             backgroundColor: "rgba(255, 255, 255, 0.1)", // Fundo branco translúcido ao passar o mouse
// //             borderColor: "#FFFFFF", // Mantém a borda branca ao hover
// //           },
// //         }}
// //         variant="outlined"
// //         aria-label="Voltar para a lista de vereadores"
// //       >
// //         Voltar
// //       </Button>

// //       <Grid container spacing={4}>
// //         {/* Painel Esquerdo */}
// //         <Grid item xs={12} md={4}>
// //           <Card
// //             sx={{
// //               padding: 2,
// //               textAlign: "center",
// //               boxShadow: 3,
// //               backgroundColor: "#f5f5f5",
// //               "&:hover": {
// //                 outline: "2px solid #FFD700", // Adiciona uma borda dourada ao receber foco
// //                 }, 
// //             }}
// //           >
// //             <img
// //               src={imagem}
// //               alt={`Imagem do vereador ${nome}`}
// //               style={{
// //                 width: "100%",
// //                 maxWidth: "200px",
// //                 height: "auto",
// //                 borderRadius: "8px",
// //               }}
// //             />
// //             <Typography
// //               variant="h5"
// //               fontWeight="bold"
// //               sx={{ marginTop: 2 }}
// //               aria-label={`Vereador: ${nome}`}
// //             >
// //               {nome}
// //             </Typography>
// //             <Typography
// //               variant="body1"
// //               color="text.secondary"
// //               aria-label={`Partido: ${partido}`}
// //             >
// //               {partido}
// //             </Typography>

// //             <Divider sx={{ marginY: 2 }} />

// //             {/* Informações de Contato */}
// //             <Typography variant="body2">
// //               <strong>Nome civil:</strong> {nomeCivil}
// //             </Typography>
// //             <Typography variant="body2">
// //               <strong>Telefone(s):</strong> {telefone}
// //             </Typography>
// //             <Typography variant="body2">
// //               <strong>Celular:</strong> {celular}
// //             </Typography>
// //             <Typography variant="body2">
// //               <strong>E-mail:</strong> {email}
// //             </Typography>
// //             <Typography variant="body2">
// //               <strong>Gabinete:</strong> {gabinete}
// //             </Typography>

// //             <Divider sx={{ marginY: 2 }} />

// //             {/* Redes Sociais */}
// //             <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
// //               {facebookLink && (
// //                 <IconButton
// //                   color="primary"
// //                   component="a"
// //                   href={facebookLink}
// //                   target="_blank"
// //                   rel="noopener noreferrer"
// //                   aria-label="Acessar perfil do Facebook"
// //                 >
// //                   <FacebookIcon sx={{ fontSize: "48px" }} />
// //                 </IconButton>
// //               )}
// //               {instagramLink && (
// //                 <IconButton
// //                   color="secondary"
// //                   component="a"
// //                   href={instagramLink}
// //                   target="_blank"
// //                   rel="noopener noreferrer"
// //                   aria-label="Acessar perfil do Instagram"
// //                 >
// //                   <InstagramIcon
// //                     sx={{ fontSize: "48px", color: "#E4405F" }}
// //                   />
// //                 </IconButton>
// //               )}
// //               {youtubeLink && (
// //                 <IconButton
// //                   color="secondary"
// //                   component="a"
// //                   href={youtubeLink}
// //                   target="_blank"
// //                   rel="noopener noreferrer"
// //                   aria-label="Acessar canal do YouTube"
// //                 >
// //                   <YoutubeIcon sx={{ fontSize: "54px", color: "#FF0000" }} />
// //                 </IconButton>
// //               )}
// //               {XLink && (
// //                 <IconButton
// //                   color="secondary"
// //                   component="a"
// //                   href={XLink}
// //                   target="_blank"
// //                   rel="noopener noreferrer"
// //                   aria-label="Acessar perfil no X (Twitter)"
// //                 >
// //                   <X sx={{ fontSize: "40px", color: "#000000" }} />
// //                 </IconButton>
// //               )}
// //               {LinkTikTok && (
// //                 <IconButton
// //                   color="secondary"
// //                   component="a"
// //                   href={LinkTikTok}
// //                   target="_blank"
// //                   rel="noopener noreferrer"
// //                   aria-label="Acessar perfil no TikTok"
// //                 >
// //                   <TikTokIcon />
// //                 </IconButton>
// //               )}
// //               {LinkSite && (
// //                 <IconButton
// //                   color="secondary"
// //                   component="a"
// //                   href={LinkSite}
// //                   target="_blank"
// //                   rel="noopener noreferrer"
// //                   aria-label="Acessar site oficial"
// //                 >
// //                   <SiteIcon sx={{ fontSize: "48px", color: "#1976D2" }} />
// //                 </IconButton>
// //               )}
// //             </Box>
// //           </Card>
// //         </Grid>

// //         {/* Painel Direito */}
// //         <Grid item xs={12} md={8}>
// //           <Card
// //             sx={
// //               { 
// //                 padding: 2,
// //                 boxShadow: 3,
// //                 backgroundColor: "#f5f5f5",
// //                 "&:hover": {
// //                 outline: "2px solid #FFD700", // Adiciona uma borda dourada ao receber foco
// //                 },
// //                }}
// //             role="region"
// //             aria-labelledby="biografia-title"
// //           >
// //             <CardContent>
// //               {/* Biografia */}
// //               <Typography
// //                 id="biografia-title"
// //                 variant="h6"
// //                 fontWeight="bold"
// //                 sx={{ marginBottom: 2 }}
// //               >
// //                 Biografia
// //               </Typography>
// //               <Typography variant="body2" color="text.secondary">
// //                 {biografia.split("\n").map((linha, index) => (
// //                   <span key={index}>
// //                     {linha}
// //                     <br />
// //                   </span>
// //                 ))}
// //               </Typography>

// //               <Divider sx={{ marginY: 2 }} />

// //               {/* Participação em Comissões */}
// //               <Typography
// //                 variant="h6"
// //                 fontWeight="bold"
// //                 sx={{ marginBottom: 2 }}
// //               >
// //                 Participação em Comissões
// //               </Typography>
// //               <Box>
// //                 {comissoes.map((comissao, index) => (
// //                   <Box key={index} sx={{ marginBottom: 1 }}>
// //                     <Typography variant="body2" >
// //                     <strong>Permanente:</strong> {comissao.permanente}
// //                     </Typography>
// //                     <Typography variant="body2" color="text.secondary">
// //                       <strong>Cargo:</strong> {comissao.cargo}
// //                     </Typography>
// //                   </Box>
// //                 ))}
// //               </Box>
// //             </CardContent>
// //           </Card>
// //         </Grid>
// //       </Grid>
// //     </Box>
// //   );
// // };

// // export default BiografiaVereador;